import React, { useState, useEffect, ChangeEvent } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Button,
  TextField,
  Box,
  CircularProgress,
  CardMedia,
  Input,
  MenuItem,
  Select,
  SelectChangeEvent,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/bootstrap.css";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import { useGetUser, useUpdateUser, useCreateUser } from "../../api/hooks/useUser";
import { showSuccess, showError } from "../../helpers/toast";
import { uploadService } from "../../api/services/uploadService";
import { getQueryClient } from "../../api/queryClient";
import { queryTags } from "../../api/queryTags";
import type { CreateUserPayload, UpdateUserPayload } from "../../api/types/user.types";

const EditUser = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const userId = location?.state?.userId || location?.state?.user?._id;
  const isAddMode = !userId;

  // Form states
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [countryCode, setCountryCode] = useState("+1");
  const [gender, setGender] = useState<number>(0);
  const [dateOfBirth, setDateOfBirth] = useState<Dayjs | null>(null);
  const [isBlocked, setIsBlocked] = useState(false);
  const [image, setImage] = useState<string>("");
  const [fileName, setFileName] = useState<string>("");
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Fetch user data (only in edit mode)
  const { data: userData, isLoading: isLoadingUser, error: userError } = useGetUser(
    userId || '',
    { enabled: !!userId && !isAddMode }
  );

  // Update form fields when user data is loaded
  useEffect(() => {
    if (userData?.data) {
      const user = userData.data;
      setFullName(user.fullName || "");
      setEmail(user.email || "");
      setPhone(user.phone || "");
      setCountryCode(user.countryCode || "+1");
      setGender(user.gender || 0);
      setDateOfBirth(user.dateOfBirth || user.dob ? dayjs(user.dateOfBirth || user.dob) : null);
      setIsBlocked(user.isBlocked || false);
      setImage(user.image || "");
    }
  }, [userData]);

  // Handle error (only in edit mode)
  useEffect(() => {
    if (userError && !isAddMode) {
      showError((userError as any)?.response?.data?.message || "Failed to load user details");
    }
  }, [userError, isAddMode]);

  // Create user mutation
  const { mutate: createUser, isPending: isCreating } = useCreateUser({
    onSuccess: (response) => {
      showSuccess(response.message || "User created successfully");
      const queryClient = getQueryClient();
      queryClient.invalidateQueries({ queryKey: [queryTags.user] });
      navigate("/manage-users");
    },
    onError: (error: any) => {
      showError(error?.response?.data?.message || "Failed to create user");
    },
  });

  // Update user mutation
  const { mutate: updateUser, isPending: isUpdating } = useUpdateUser({
    onSuccess: (response) => {
      showSuccess(response.message || "User updated successfully");
      const queryClient = getQueryClient();
      queryClient.invalidateQueries({ queryKey: [queryTags.user] });
      navigate("/manage-users");
    },
    onError: (error: any) => {
      showError(error?.response?.data?.message || "Failed to update user");
    },
  });

  // Validation
  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!fullName.trim()) {
      newErrors.fullName = "Full name is required";
    }

    if (!email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = "Please enter a valid email address";
    }

    const numericPhone = phone.replace(/\D/g, "");
    if (!numericPhone) {
      newErrors.phone = "Phone number is required";
    } else if (numericPhone.length < 9 || numericPhone.length > 13) {
      newErrors.phone = "Phone must be between 9 and 13 digits";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    const numericPhone = phone.replace(/\D/g, "");

    const originalImage =
      userData?.data?.image ||
      (userData?.data as any)?.profileImage ||
      (userData?.data as any)?.profilePhoto ||
      "";

    let imageUrl = image;
    if (imageFile) {
      try {
        setUploadingImage(true);
        const formData = new FormData();
        formData.append('file', imageFile);
        const uploadResponse = await uploadService.uploadFile(formData);
        if (uploadResponse?.data && typeof uploadResponse.data === 'string') {
          imageUrl = uploadResponse.data;
        }
        if (imageUrl && imageUrl !== image) {
          showSuccess("Image uploaded successfully");
        }
      } catch (error: any) {
        showError(error?.response?.data?.message || "Failed to upload image");
        setUploadingImage(false);
        return;
      } finally {
        setUploadingImage(false);
      }
    }

    if (isAddMode) {
      // Create user
      const payload: CreateUserPayload = {
        fullName: fullName.trim(),
        email: email.trim(),
        countryCode: countryCode,
        phone: numericPhone,
        gender: gender ? gender.toString() : undefined,
        dob: dateOfBirth ? dateOfBirth.format('YYYY-MM-DD') : undefined,
        isBlocked: isBlocked,
        role: 3
      };

      if (imageUrl) {
        payload.image = imageUrl;
      }

      createUser(payload);
    } else {
      // Update user
      const payload: UpdateUserPayload = {
        fullName: fullName.trim(),
        email: email.trim(),
        countryCode: countryCode,
        phone: numericPhone,
        gender: gender.toString(),
        dob: dateOfBirth ? dateOfBirth.format('YYYY-MM-DD') : '',
        isBlocked: isBlocked,
      };

      if (imageUrl && imageUrl !== originalImage) {
        payload.image = imageUrl;
      } else if (!imageUrl && originalImage) {
        // User cleared the image
        payload.image = "";
      }

      updateUser({ id: userId, data: payload });
    }
  };

  if (isLoadingUser && !isAddMode) {
    return (
      <>
        <div className="main_title">
          <h1>{isAddMode ? "Add User" : "Edit User"}</h1>
          <p>
            <button onClick={() => navigate("/dashboard")}>Dashboard</button> -
            <button onClick={() => navigate("/manage-users")}> Manage Users</button> - {isAddMode ? "Add User" : "Edit User"}
          </p>
        </div>
        <Box display="flex" justifyContent="center" alignItems="center" p={4}>
          <CircularProgress />
        </Box>
      </>
    );
  }

  return (
    <>
      <div className="main_title">
        <h1>{isAddMode ? "Add User" : "Edit User"}</h1>
        <p>
          <button onClick={() => navigate("/dashboard")}>Dashboard</button> -
          <button onClick={() => navigate("/manage-users")}> Manage Users</button> - {isAddMode ? "Add User" : "Edit User"}
        </p>
      </div>

      <div className="cards">
        <div className="form">
          <form onSubmit={handleSubmit}>
            <div className="gap_p">
              {/* PROFILE IMAGE */}
              <div className="control_group w_100">
                <label htmlFor="icon-button-file">
                  Profile Image
                </label>
                {image ? (
                  <div className="upload_image">
                    <div className="upload_image_holder no_radius v2">
                      <figure>
                        <CardMedia component="img" image={image} alt="photo" />
                      </figure>
                      <CloseIcon
                        onClick={() => {
                          setImage("");
                          setFileName("");
                          setImageFile(null);
                        }}
                      />
                    </div>
                  </div>
                ) : (
                  <label className="upload_image" htmlFor="icon-button-file">
                    <Input
                      sx={{ display: "none" }}
                      id="icon-button-file"
                      type="file"
                      inputProps={{ accept: "image/png,image/jpeg" }}
                      onChange={(event: ChangeEvent<HTMLInputElement>) => {
                        const files = event.target.files;
                        if (files && files[0].type.includes("image")) {
                          setFileName(files[0].name);
                          setImage(URL.createObjectURL(files[0]));
                          setImageFile(files[0]);
                        }
                      }}
                    />
                    <span className="upload_image_holder no_radius">
                      <figure className="add_img">
                        <img src="/static/images/placeholder.png" alt="icon" />
                        <span className="upload_img_icon">
                          <img src="/static/images/edit2_icon.svg" alt="icon" />
                        </span>
                      </figure>
                    </span>
                  </label>
                )}
              </div>

              {/* FULL NAME */}
              <div className="control_group w_33">
                <label>Full Name <span className="req">*</span></label>
                <TextField
                  placeholder="Enter Full Name"
                  fullWidth
                  hiddenLabel
                  value={fullName}
                  onChange={(e) => {
                    setFullName(e.target.value);
                    if (errors.fullName) {
                      setErrors({ ...errors, fullName: "" });
                    }
                  }}
                  error={!!errors.fullName}
                  helperText={errors.fullName}
                  required
                  disabled={(isUpdating || isCreating) || uploadingImage}
                />
              </div>

              {/* EMAIL */}
              <div className="control_group w_33">
                <label>Email <span className="req">*</span></label>
                <TextField
                  type="email"
                  placeholder="Enter Email"
                  fullWidth
                  hiddenLabel
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (errors.email) {
                      setErrors({ ...errors, email: "" });
                    }
                  }}
                  error={!!errors.email}
                  helperText={errors.email}
                  required
                  disabled={(isUpdating || isCreating) || uploadingImage}
                />
              </div>

              {/* PHONE */}
              <div className="control_group w_33">
                <label>Phone Number <span className="req">*</span></label>
                <PhoneInput
                  country={"us"}
                  placeholder="Enter Phone Number"
                  enableSearch={false}
                  autoFormat={false}
                  inputStyle={{ width: "100%" }}
                  value={`${countryCode.replace("+", "")}${phone}`}
                  onChange={(value, data: any) => {
                    const dial = data?.dialCode || "";
                    const withoutDial = value.replace(dial, "");
                    setPhone(withoutDial);
                    setCountryCode(`+${dial}`);
                    if (errors.phone) {
                      setErrors({ ...errors, phone: "" });
                    }
                  }}
                  disabled={(isUpdating || isCreating) || uploadingImage}
                />
                {errors.phone && (
                  <h6 className="err_msg">{errors.phone}</h6>
                )}
              </div>

              {/* GENDER */}
              <div className="control_group w_33">
                <label>Gender</label>
                <Select
                  fullWidth
                  value={gender === 0 ? "" : gender.toString()}
                  onChange={(e: SelectChangeEvent) => {
                    setGender(Number(e.target.value));
                    if (errors.gender) {
                      setErrors({ ...errors, gender: "" });
                    }
                  }}
                  displayEmpty
                  renderValue={(selected) => {
                    if (!selected || selected === "") {
                      return <span style={{ color: "#999" }}>Select Gender</span>;
                    }
                    if (selected === "1") return "Male";
                    if (selected === "2") return "Female";
                    if (selected === "3") return "Other";
                    return selected;
                  }}
                  disabled={(isUpdating || isCreating) || uploadingImage}
                  sx={{ height: '56px' }}
                >
                  <MenuItem disabled value="">
                    <em>Select Gender</em>
                  </MenuItem>
                  <MenuItem value={1}>Male</MenuItem>
                  <MenuItem value={2}>Female</MenuItem>
                  <MenuItem value={3}>Other</MenuItem>
                </Select>
                {errors.gender && (
                  <h6 className="err_msg">{errors.gender}</h6>
                )}
              </div>

              {/* DATE OF BIRTH */}
              <div className="control_group w_33">
                <label>Date of Birth</label>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    value={dateOfBirth}
                    onChange={(newValue) => {
                      setDateOfBirth(newValue);
                      if (errors.dateOfBirth) {
                        setErrors({ ...errors, dateOfBirth: "" });
                      }
                    }}
                    format="DD-MM-YYYY"
                    maxDate={dayjs()}
                    disabled={(isUpdating || isCreating) || uploadingImage}
                    slots={{
                      openPickerIcon: () => (
                        <img src="/static/images/date_icon.svg" alt="Pick date" />
                      ),
                    }}
                    slotProps={{
                      textField: {
                        placeholder: "DD-MM-YYYY",
                        fullWidth: true,
                        hiddenLabel: true,
                        error: !!errors.dateOfBirth,
                        helperText: errors.dateOfBirth,
                      },
                    }}
                    desktopModeMediaQuery="(min-width:0px)"
                  />
                </LocalizationProvider>
              </div>

              {/* STATUS */}
              <div className="control_group w_33">
                <label>Status</label>
                <Select
                  fullWidth
                  value={isBlocked ? "blocked" : "active"}
                  onChange={(e: SelectChangeEvent) => setIsBlocked(e.target.value === "blocked")}
                  disabled={(isUpdating || isCreating) || uploadingImage}
                  sx={{ height: '56px' }}
                >
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="blocked">Blocked</MenuItem>
                </Select>
              </div>

            </div>

            {/* SAVE BUTTON */}
            <div className="form_btn">
              <Button
                className="btn btn_primary"
                type="submit"
                disabled={isUpdating || uploadingImage}
              >
                {uploadingImage ? "Uploading image..." : (isUpdating || isCreating) ? (isAddMode ? "Creating..." : "Saving...") : (isAddMode ? "Create User" : "Save")}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default EditUser;
